import { createFileRoute } from "@tanstack/react-router";
import Index from "@/pages/Index";
import { cardsPreviewQueryOptions, homepageCardFactsQueryOptions, publishedCardCountQueryOptions } from "@/hooks/useCards";
import { offersQueryOptions } from "@/hooks/useOffers";
import { adPlacementsQueryOptions } from "@/hooks/useAdPlacements";
import { cardOverridesQueryOptions } from "@/hooks/useCardOverrides";
import { homepageSettingsQueryOptions } from "@/hooks/useHomepageSettings";
import { cardListingSettingsQueryOptions } from "@/hooks/useCardListingSettings";
import { feedSettingsQueryOptions } from "@/hooks/useFeedSettings";
import { currentYear, reviewDateISO } from "@/lib/currentYear";
import { homepageFaqJsonLd } from "@/lib/homepageFaq";
import { SITE_ORIGIN } from "@/lib/routeSeo";

const homepageTitle = () => `Kreditkarten-Vergleich ${currentYear()}: Kreditkarten in Deutschland`;
const homepageDescription = () =>
  `Kreditkarten ${currentYear()} vergleichen: Jahresgebühren, Kartentypen, Zinsen und Leistungen transparent gegenüberstellen.`;

export const Route = createFileRoute("/")({
  // Fetch the comparison data on the server so the card list is part of the
  // initial HTML instead of a second round trip from the browser.
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(cardsPreviewQueryOptions),
      context.queryClient.ensureQueryData(publishedCardCountQueryOptions),
      context.queryClient.ensureQueryData(homepageCardFactsQueryOptions),
      context.queryClient.ensureQueryData(offersQueryOptions),
      context.queryClient.ensureQueryData(adPlacementsQueryOptions),
      context.queryClient.ensureQueryData(cardOverridesQueryOptions),
      context.queryClient.ensureQueryData(homepageSettingsQueryOptions),
      context.queryClient.ensureQueryData(cardListingSettingsQueryOptions),
      context.queryClient.ensureQueryData(feedSettingsQueryOptions),
    ].map((p) => p.catch(() => null)));

    const cards = context.queryClient.getQueryData<any[]>(cardsPreviewQueryOptions.queryKey) ?? [];
    const placements = context.queryClient.getQueryData<any[]>(adPlacementsQueryOptions.queryKey) ?? [];
    const overrides = context.queryClient.getQueryData<any[]>(cardOverridesQueryOptions.queryKey) ?? [];
    const sponsoredIds = new Set(placements.map((placement) => placement.card_id).filter(Boolean));
    const overridesByCard = new Map<string, Record<string, any>>();
    [...overrides]
      .sort((a, b) => new Date(a.effective_from || a.created_at || 0).getTime() - new Date(b.effective_from || b.created_at || 0).getTime())
      .forEach((override) => {
        if (!override.card_id) return;
        const current = overridesByCard.get(override.card_id) ?? {};
        const rawValue = override.value_override;
        current[override.field_name] = rawValue && typeof rawValue === "object" && "value" in rawValue
          ? rawValue.value
          : rawValue;
        overridesByCard.set(override.card_id, current);
      });
    const rankedCards = cards.map((card) => ({ ...card, ...(overridesByCard.get(card.id) ?? {}) }));
    const benefitScore = (card: any) =>
      (card.annual_fee === 0 ? 2 : 0) +
      (((card.fx_fee_intl_percent ?? card.foreign_tx_fee_percent) === 0) ? 1 : 0) +
      ((card.travel_insurance || card.travel_health_insurance) ? 1 : 0) +
      ((card.cashback_program || card.bonus_program) ? 1 : 0) +
      (card.nfc_contactless ? 0.2 : 0);
    const topCards = rankedCards
      .sort((a, b) => {
        const sponsoredDifference = Number(sponsoredIds.has(b.id)) - Number(sponsoredIds.has(a.id));
        return sponsoredDifference || benefitScore(b) - benefitScore(a);
      })
      .slice(0, 8)
      .map((card) => ({ slug: card.slug, name: card.display_name || card.name }));

    return { topCards };
  },
  head: ({ loaderData }) => {
    const title = homepageTitle();
    const description = homepageDescription();
    const topCards = loaderData?.topCards ?? [];
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${SITE_ORIGIN}/#kartenvergleich`,
      name: `Kreditkarten im Vergleich ${currentYear()}`,
      numberOfItems: topCards.length,
      itemListElement: topCards.map((card, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: card.name,
        url: `${SITE_ORIGIN}/kreditkarten/${card.slug}`,
      })),
    };
    const collectionPage = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      url: `${SITE_ORIGIN}/`,
      description,
      dateModified: reviewDateISO(),
      mainEntity: { "@id": `${SITE_ORIGIN}/#kartenvergleich` },
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE_ORIGIN}/` },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: `${SITE_ORIGIN}/` }],
      scripts: [collectionPage, itemList, homepageFaqJsonLd()].map((schema) => ({
        type: "application/ld+json",
        children: JSON.stringify(schema),
      })),
    };
  },
  component: Index,
  errorComponent: ({ error }) => <div role="alert">Die Seite konnte nicht geladen werden: {error.message}</div>,
  notFoundComponent: () => <div>Diese Seite wurde nicht gefunden.</div>,
});
